import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { MindARThree } from '../utils/mindarWithQuality';
import ErrorScreen from './ErrorScreen';
import ScanningScreen from './ScanningScreen';
import './ARViewer.css';

const BASE_URL = process.env.PUBLIC_URL + '/resources/';
const TARGETS_PATH = process.env.REACT_APP_TARGETS
  ? BASE_URL + process.env.REACT_APP_TARGETS
  : process.env.PUBLIC_URL + '/resources/targets.mind';
const VIDEO_SCALE = Number(process.env.REACT_APP_VIDEO_SCALE) || 1.5;
const VIDEO_ASPECT = Number(process.env.REACT_APP_VIDEO_ASPECT) || 1.5;

const TRACKING_CONFIG = {
  filterMinCF: 0.010,
  filterBeta: 30000,
  warmupTolerance: 1,
  missTolerance: 25,
};

const VIDEO_SOURCES = [
  'Experiencia_4toMasterClass_Prueba2.webm',
  'Experiencia_4toMasterClass_Prueba2.mp4',
];

function ARViewer() {
  const containerRef = useRef(null);
  const mindarRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const streamRef = useRef(null);
  const recordingCompositeRef = useRef(null);

  const [status, setStatus] = useState('scanning'); // Start directly in scanning mode
  const [error, setError] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let mindarThree = null;
    let videoElement = null;

    const initAR = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Tu navegador no soporta el acceso a la cámara.');
        }
        const permissionStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        permissionStream.getTracks().forEach((track) => track.stop());

        mindarThree = new MindARThree({
          container,
          imageTargetSrc: TARGETS_PATH,
          maxTrack: 1,
          ...TRACKING_CONFIG,
        });
        mindarRef.current = mindarThree;

        const { renderer, scene, camera } = mindarThree;
        const anchor = mindarThree.addAnchor(0);

        videoElement = document.createElement('video');
        videoElement.loop = true;
        videoElement.muted = true;
        videoElement.playsInline = true;
        videoElement.setAttribute('webkit-playsinline', '');

        const videoTexture = new THREE.VideoTexture(videoElement);
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTexture.format = THREE.RGBAFormat;
        videoTexture.colorSpace = THREE.SRGBColorSpace;

        const planeGeometry = new THREE.PlaneGeometry(VIDEO_SCALE * VIDEO_ASPECT, VIDEO_SCALE);
        const planeMaterial = new THREE.MeshBasicMaterial({ map: videoTexture, transparent: true });
        const plane = new THREE.Mesh(planeGeometry, planeMaterial);
        anchor.group.add(plane);

        let videoLoaded = false;
        const loadVideo = (src) => new Promise((resolve, reject) => {
          videoElement.onloadeddata = () => resolve(true);
          videoElement.onerror = () => reject(`Failed to load video: ${src}`);
          videoElement.src = src;
        });

        const videoLoadPromise = (async () => {
          for (const src of VIDEO_SOURCES) {
            try {
              await loadVideo(BASE_URL + src);
              console.log(`Successfully loaded video: ${src}`);
              videoLoaded = true;
              return;
            } catch (e) {
              console.warn(e);
            }
          }
          throw new Error('None of the video sources could be loaded.');
        })();

        anchor.onTargetFound = () => {
          setIsTracking(true);
          if (videoLoaded) videoElement.play().catch(e => console.error("Video play failed", e));
        };

        anchor.onTargetLost = () => {
          setIsTracking(false);
          if (videoLoaded) videoElement.pause();
        };

        await Promise.all([mindarThree.start(), videoLoadPromise]);
        
        renderer.setAnimationLoop(() => {
          renderer.render(scene, camera);
          const rec = recordingCompositeRef.current;
          if (rec?.canvas && rec.ctx && rec.video && rec.glCanvas) {
            const { canvas, ctx, video, glCanvas } = rec;
            
            if (video.videoWidth === 0 || video.videoHeight === 0) return;

            const videoAspect = video.videoWidth / video.videoHeight;
            const recordingAspect = canvas.width / canvas.height;

            let drawWidth, drawHeight, x, y;

            if (videoAspect > recordingAspect) {
              drawWidth = canvas.width;
              drawHeight = drawWidth / videoAspect;
              x = 0;
              y = (canvas.height - drawHeight) / 2;
            } else {
              drawHeight = canvas.height;
              drawWidth = drawHeight * videoAspect;
              y = 0;
              x = (canvas.width - drawWidth) / 2;
            }
            
            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(video, x, y, drawWidth, drawHeight);
            ctx.drawImage(glCanvas, x, y, drawWidth, drawHeight);
          }
        });

      } catch (err) {
        console.error('Error initializing MindAR:', err);
        setError(err.message || 'Error initializing AR session');
        setStatus('error');
      }
    };

    initAR();

    return () => {
      mediaRecorderRef.current?.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      mindarRef.current?.stop();
    };
  }, []);

  const startRecording = async () => {
    try {
      const mindar = mindarRef.current;
      const glCanvas = mindar?.renderer?.domElement;
      const cameraVideo = mindar?.video;

      if (!glCanvas || !cameraVideo || typeof glCanvas.captureStream !== 'function') {
        throw new Error('La grabación no está disponible en este dispositivo.');
      }

      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      
      const compositeCanvas = document.createElement('canvas');
      if (isPortrait) {
        compositeCanvas.width = 1080;
        compositeCanvas.height = 1920;
      } else {
        compositeCanvas.width = 1920;
        compositeCanvas.height = 1080;
      }

      const ctx = compositeCanvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      recordingCompositeRef.current = { canvas: compositeCanvas, ctx, video: cameraVideo, glCanvas };
      
      const stream = compositeCanvas.captureStream(30);
      streamRef.current = stream;
      
      const options = {
        videoBitsPerSecond: 8 * 1024 * 1024, // 8 Mbps
      };
      
      if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        options.mimeType = 'video/webm;codecs=vp9';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
        options.mimeType = 'video/webm;codecs=vp8';
      } else {
        options.mimeType = 'video/webm';
      }
      
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        recordingCompositeRef.current = null;
        const blob = new Blob(recordedChunksRef.current, { type: options.mimeType.split(';')[0] });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ar-recording-${Date.now()}.webm`;
        a.click();
        URL.revokeObjectURL(url);
      };

      mediaRecorder.start();
      setIsRecording(true);

    } catch (err) {
      console.error('Error starting recording:', err);
      alert(err.message || 'No se pudo iniciar la grabación.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    setStatus('scanning');
    window.location.reload(); // Reloading is a simple way to re-init
  };

  return (
    <div className="ar-viewer">
      {status === 'error' && <ErrorScreen error={error} onRetry={handleRetry} />}
      <div ref={containerRef} className={`ar-container ${status !== 'scanning' ? 'hidden' : ''}`} />
      {status === 'scanning' && (
        <ScanningScreen
          isTracking={isTracking}
          isRecording={isRecording}
          onStartRecording={startRecording}
          onStopRecording={stopRecording}
        />
      )}
    </div>
  );
}

export default ARViewer;
